import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody, Button, Chip, TextField,
  MenuItem, Alert, Stack, Paper, Switch, FormControlLabel, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TablePagination, Tooltip, CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../services/api';

// Customer-facing rate card: sell rates only, in the customer's sheet format.
// A portal rate change on the customer's plan e-mails the updated sheet automatically (when Auto e-mail is on).

interface CustTenant {
  id: string; name: string; status: string; plan_name: string; enabled: boolean; auto_email: boolean;
  email_to: string | null; last_sent_at: string | null; pending_changes: number;
}
interface Row {
  country: string; destination: string; code: string; time_band: string; effective_date: string | null;
  rate: number; billing_increment: string; status: string; previous_rate: number | null;
}

const statusColor = (s: string): any =>
  s === 'Increase' ? 'error' : s === 'Decrease' ? 'success' : s === 'New' ? 'info' : s === 'Removed' ? 'warning' : 'default';
const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'UTC' }) : '—';
const fmtDateTime = (s: string | null) => (s ? new Date(s).toLocaleString('en-GB', { timeZone: 'UTC' }) + ' UTC' : '—');

export const CustomerRateCardTab: React.FC = () => {
  const [tenants, setTenants] = useState<CustTenant[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [card, setCard] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [changesOnly, setChangesOnly] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(50);

  const loadTenants = useCallback(async () => {
    try {
      const { data } = await api.get('/customer-ratecard/tenants');
      setTenants(data || []);
      setSelected((cur) => cur || (data || []).find((t: CustTenant) => t.enabled)?.id || '');
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.message || 'Failed to load customers' });
    }
  }, []);

  const loadCard = useCallback(async (id: string) => {
    if (!id) return;
    setLoading(true);
    try {
      const [c, h] = await Promise.all([api.get(`/customer-ratecard/${id}`), api.get(`/customer-ratecard/${id}/history`)]);
      setCard(c.data);
      setHistory(h.data || []);
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.message || 'Failed to load rate card' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);
  useEffect(() => { setPage(0); loadCard(selected); }, [selected, loadCard]);

  const refresh = () => { loadTenants(); loadCard(selected); };

  const updateSettings = async (patch: any) => {
    setBusy(true); setMsg(null);
    try {
      await api.put(`/customer-ratecard/${selected}/settings`, patch);
      refresh();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.message || 'Failed to save' });
    } finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true); setMsg(null);
    try {
      const res = await api.get(`/customer-ratecard/${selected}/xlsx`, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = /filename\*=UTF-8''([^;]+)/.exec(cd) || /filename="([^"]+)"/.exec(cd);
      const name = m ? decodeURIComponent(m[1]) : 'Origination Rates.xlsx';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      setMsg({ type: 'error', text: 'Download failed' });
    } finally { setBusy(false); }
  };

  const send = async () => {
    setConfirmSend(false); setBusy(true); setMsg(null);
    try {
      const { data } = await api.post(`/customer-ratecard/${selected}/send`, {});
      setMsg({ type: 'success', text: `Rate card e-mailed to ${data.to.join(', ')} (cc ${data.cc.join(', ')}) — ${data.changes} change(s).` });
      refresh();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.message || 'Send failed' });
    } finally { setBusy(false); }
  };

  const rows: Row[] = useMemo(() => {
    const all: Row[] = card?.rows || [];
    const q = search.trim().toLowerCase();
    return all.filter((r) =>
      (!changesOnly || r.status !== 'Current') &&
      (!q || r.country.toLowerCase().includes(q) || r.destination.toLowerCase().includes(q) || r.code.includes(q)));
  }, [card, search, changesOnly]);

  const t = tenants.find((x) => x.id === selected);

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <TextField select size="small" label="Customer" value={selected} onChange={(e) => setSelected(e.target.value)} sx={{ minWidth: 280 }}>
          {tenants.map((x) => (
            <MenuItem key={x.id} value={x.id}>
              {x.name}{x.enabled ? '' : ' (not enabled)'}{x.pending_changes ? ` — ${x.pending_changes} pending` : ''}
            </MenuItem>
          ))}
        </TextField>
        {t ? (
          <>
            <FormControlLabel
              control={<Switch checked={!!t.enabled} disabled={busy} onChange={(e) => updateSettings({ enabled: e.target.checked })} />}
              label="Customer-format rate card"
            />
            <Tooltip title="E-mail the updated sheet automatically ~2 minutes after a rate on this customer's plan is changed in the portal">
              <FormControlLabel
                control={<Switch checked={!!t.auto_email} disabled={busy || !t.enabled} onChange={(e) => updateSettings({ auto_email: e.target.checked })} />}
                label="Auto e-mail on rate change"
              />
            </Tooltip>
          </>
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={refresh}>Refresh</Button>
      </Stack>

      {msg ? <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert> : null}
      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

      {card && t ? (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary">Rate plan</Typography>
              <Typography fontWeight={600}>{card.tenant.plan_name || '—'} ({card.tenant.currency})</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary">Changes not yet sent</Typography>
              <Typography fontWeight={600} color={card.changes ? 'warning.main' : 'text.primary'}>{card.changes}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary">Last sent</Typography>
              <Typography fontWeight={600}>{fmtDateTime(t.last_sent_at)}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ px: 2, py: 1, minWidth: 240 }}>
              <Typography variant="caption" color="text.secondary">E-mail to / cc</Typography>
              <Typography fontWeight={600} sx={{ wordBreak: 'break-all' }}>{card.tenant.to.join(', ') || 'No rate-change or billing e-mail set'}</Typography>
              <Typography variant="caption" color="text.secondary">cc {card.tenant.cc.join(', ')}</Typography>
            </Paper>
          </Stack>

          {!t.enabled ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Not enabled for this customer. Turning it on records today's rates as what the customer already has (no e-mail is sent).
            </Alert>
          ) : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
            <TextField size="small" placeholder="Search country, destination or code" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ width: 320 }} />
            <FormControlLabel control={<Switch checked={changesOnly} onChange={(e) => { setChangesOnly(e.target.checked); setPage(0); }} />} label="Changes only" />
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="outlined" startIcon={busy ? <CircularProgress size={16} /> : <DownloadIcon />} disabled={busy} onClick={download}>
              Download Excel
            </Button>
            <Button variant="contained" startIcon={<SendIcon />} disabled={busy || !t.enabled || !card.tenant.to.length} onClick={() => setConfirmSend(true)}>
              Send to customer
            </Button>
          </Stack>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Country', 'Destination', 'Codes', 'Time Band', 'Effective Date', 'Rates', 'Billing Increment', 'Status'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(page * rpp, page * rpp + rpp).map((r) => (
                    <TableRow key={`${r.code}|${r.time_band}|${r.status}`} hover>
                      <TableCell>{r.country}</TableCell>
                      <TableCell>{r.destination}</TableCell>
                      <TableCell>{r.code}</TableCell>
                      <TableCell>{r.time_band}</TableCell>
                      <TableCell>{fmtDate(r.effective_date)}</TableCell>
                      <TableCell>
                        {Number(r.rate).toFixed(4)}
                        {r.previous_rate != null && r.previous_rate !== r.rate && r.status !== 'Removed' ? (
                          <Typography component="span" variant="caption" color="text.secondary"> (was {Number(r.previous_rate).toFixed(4)})</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{r.billing_increment}</TableCell>
                      <TableCell><Chip size="small" label={card.has_baseline ? r.status : 'New'} color={statusColor(card.has_baseline ? r.status : 'New')} variant={r.status === 'Current' ? 'outlined' : 'filled'} /></TableCell>
                    </TableRow>
                  ))}
                  {!rows.length ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No rates</TableCell></TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Box>
            <TablePagination component="div" count={rows.length} page={page} onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rpp} onRowsPerPageChange={(e) => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 500]} />
          </Card>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>History</Typography>
          <Card variant="outlined">
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['When', 'Type', 'Status', 'To', 'File', 'Rows', 'Changes', 'By'].map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell>{fmtDateTime(h.created_at)}</TableCell>
                      <TableCell>{h.kind}</TableCell>
                      <TableCell>
                        <Tooltip title={h.error || ''}><Chip size="small" label={h.status} color={h.status === 'sent' ? 'success' : h.status === 'failed' ? 'error' : 'default'} /></Tooltip>
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-all' }}>{h.recipients || '—'}</TableCell>
                      <TableCell>{h.file_name || '—'}</TableCell>
                      <TableCell>{h.total_rows ?? '—'}</TableCell>
                      <TableCell>{h.changes ?? '—'}</TableCell>
                      <TableCell>{h.sent_by_email || (h.kind === 'auto' ? 'automatic' : '—')}</TableCell>
                    </TableRow>
                  ))}
                  {!history.length ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>Nothing sent yet</TableCell></TableRow> : null}
                </TableBody>
              </Table>
            </Box>
          </Card>
        </>
      ) : null}

      <Dialog open={confirmSend} onClose={() => setConfirmSend(false)}>
        <DialogTitle>Send rate card to {card?.tenant?.name}?</DialogTitle>
        <DialogContent>
          <Typography>
            The full rate card ({card?.rows?.length || 0} rows, {card?.changes || 0} change(s)) will be e-mailed as an Excel sheet to{' '}
            <b>{card?.tenant?.to?.join(', ')}</b>, cc {card?.tenant?.cc?.join(', ')}. Sell rates only.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSend(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={send}>Send</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerRateCardTab;
